import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import AuthPage from "./pages/AuthPage";
import QuestionnairePage from "./pages/QuestionnairePage";
import DashboardPage from "./pages/DashboardPage";
import ProfilePage from "./pages/ProfilePage";
import CapScorePage from "./pages/CapScorePage";
import FinancesPage from "./pages/FinancesPage";
import PatrimoinePage from "./pages/PatrimoinePage";
import ObjectivesPage from "./pages/ObjectivesPage";
import MarketsPage from "./pages/MarketsPage";
import SuperInvestorsPage from "./pages/SuperInvestorsPage";
import CommunityPage from "./pages/CommunityPage";
import AnalysePage from "./pages/analyse/AnalysePage";
import OutilsPage from "./pages/OutilsPage";
import AdminPage from "./pages/AdminPage";
import "./App.css";

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cap-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cap-emerald border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  // Redirect to questionnaire if profile not completed
  if (user && !user.profile_completed && window.location.pathname !== '/questionnaire') {
    return <Navigate to="/questionnaire" replace />;
  }
  
  return children;
};

// Public route (redirect to home if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cap-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cap-emerald border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/auth" 
        element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } 
      />
      
      {/* Questionnaire (protected but no layout) */}
      <Route 
        path="/questionnaire" 
        element={
          <ProtectedRoute>
            <QuestionnairePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Protected routes with layout */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/cap-score" 
        element={
          <ProtectedRoute>
            <Layout>
              <CapScorePage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/finances" 
        element={
          <ProtectedRoute>
            <Layout>
              <FinancesPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patrimoine" 
        element={
          <ProtectedRoute>
            <Layout>
              <PatrimoinePage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/objectives" 
        element={
          <ProtectedRoute>
            <Layout>
              <ObjectivesPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/markets" 
        element={
          <ProtectedRoute>
            <Layout>
              <MarketsPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/super-investors" 
        element={
          <ProtectedRoute>
            <Layout>
              <SuperInvestorsPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/community" 
        element={
          <ProtectedRoute>
            <Layout>
              <CommunityPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analyse" 
        element={
          <ProtectedRoute>
            <Layout>
              <AnalysePage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outils" 
        element={
          <ProtectedRoute>
            <Layout>
              <OutilsPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#1a1d27',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#ffffff',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
